import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

/*
 * Royalty-free low-poly props (Kenney Nature Kit, CC0, see public/models/farm). Each model is turned
 * into InstancedMeshes, one per mesh it is made of, so a whole orchard costs a handful of draw calls.
 */

const loader = new GLTFLoader();
const cache = new Map<string, Promise<THREE.Object3D>>();

const load = (name: string): Promise<THREE.Object3D> => {
    let p = cache.get(name);
    if (!p) {
        p = loader
            .loadAsync(
                `${process.env.PUBLIC_URL ?? ''}/models/farm/${name}.glb`
            )
            .then((g) => {
                g.scene.updateMatrixWorld(true);
                return g.scene;
            });
        cache.set(name, p);
    }
    return p;
};

export interface Placement {
    x: number;
    y: number;
    z: number;
    /** Rotation about the vertical axis. */
    ry?: number;
    scale?: number;
    /** Optional per-axis scale on top of `scale`. */
    sy?: number;
    /** Tints the instance (multiplies the model's own colours). */
    tint?: THREE.Color;
}

/** Adds every placement of `name` to `root`. Resolves to nothing if the model cannot be loaded. */
export const placeModel = async (
    root: THREE.Object3D,
    name: string,
    placements: Placement[],
    isAlive: () => boolean = () => true
): Promise<void> => {
    if (!placements.length) return;
    let model: THREE.Object3D;
    try {
        model = await load(name);
    } catch {
        return;
    }
    if (!isAlive()) return;

    const inst = new THREE.Matrix4();
    const rot = new THREE.Matrix4();
    const scl = new THREE.Matrix4();
    const out = new THREE.Matrix4();
    model.traverse((o) => {
        const mesh = o as THREE.Mesh;
        if (!mesh.isMesh) return;
        const src = mesh.material as THREE.MeshStandardMaterial;
        const mat = new THREE.MeshStandardMaterial({
            color: src.color,
            map: src.map,
            vertexColors: src.vertexColors,
            roughness: 1,
            metalness: 0,
        });
        const im = new THREE.InstancedMesh(
            mesh.geometry,
            mat,
            placements.length
        );
        placements.forEach((p, i) => {
            const s = p.scale ?? 1;
            inst.makeTranslation(p.x, p.y, p.z);
            rot.makeRotationY(p.ry ?? 0);
            scl.makeScale(s, s * (p.sy ?? 1), s);
            inst.multiply(rot).multiply(scl);
            out.multiplyMatrices(inst, mesh.matrixWorld);
            im.setMatrixAt(i, out);
            if (p.tint) im.setColorAt(i, p.tint);
        });
        im.instanceMatrix.needsUpdate = true;
        if (im.instanceColor) im.instanceColor.needsUpdate = true;
        im.frustumCulled = false;
        root.add(im);
    });
};

/** A single, unshared copy of a model, for the one-off pieces that need to move. */
export const modelGroup = async (name: string): Promise<THREE.Group | null> => {
    let model: THREE.Object3D;
    try {
        model = await load(name);
    } catch {
        return null;
    }
    const g = new THREE.Group();
    const copy = model.clone(true);
    copy.traverse((o) => {
        const mesh = o as THREE.Mesh;
        if (!mesh.isMesh) return;
        const src = mesh.material as THREE.MeshStandardMaterial;
        mesh.material = new THREE.MeshStandardMaterial({
            color: src.color,
            map: src.map,
            vertexColors: src.vertexColors,
            roughness: 1,
            metalness: 0,
        });
    });
    g.add(copy);
    return g;
};
